import { Alert, Flex } from '@conteg/ui';
import { useTranslation } from 'react-i18next';

import {
  MAX_TWO_FACTOR_ATTEMPTS,
  remainingTwoFactorAttempts,
  twoFactorAttemptsAlertType,
} from './two-factor-attempts';

/**
 * Shown above any action that would release a parcel the carrier protects with a recipient release code
 * (UPS EPRA) before that code has been verified — a forced open, a state change, a cancellation, a
 * shortened window. The operator reaching such a screen already holds the dedicated role, so this is not
 * a refusal; it is there so nobody performs one of these by habit without noticing what the parcel is.
 *
 * With `failedAttempts` it also says how the recipient stands - the same counter the pin dialog shows,
 * so the operator sees on this screen too that the parcel is already on its way back to the sender
 * (OXIT-737) rather than finding out after releasing it. Shown only once a code has been refused: with
 * none spent the general warning above already says everything there is to say.
 *
 * Alert rather than Notification on purpose: Notification hides itself after a few seconds, and a
 * warning about bypassing a carrier's release authorisation has to still be on screen when the operator
 * confirms.
 */
export const TwoFactorWarning = ({
  isTwoFactorPending,
  failedAttempts,
}: {
  isTwoFactorPending: boolean;
  /** Codes already refused for this reservation; omitted where the caller does not know the reservation. */
  failedAttempts?: number;
}) => {
  const { t } = useTranslation();

  if (!isTwoFactorPending) return null;

  const remainingAttempts =
    failedAttempts === undefined || failedAttempts === 0
      ? undefined
      : remainingTwoFactorAttempts(failedAttempts);

  return (
    // Kept clear of the modal's top-right corner, where its close button sits. The button is positioned
    // 6.75rem from the edge inside an 8rem padding, so it should stay in the padding - but its icon is a
    // fixed 16px, which reaches into the content area at this app's root font size. Being the first
    // element in the modal, a full-width alert would otherwise run underneath it (OXIT-171).
    <Flex mb="4rem" width="100%" pr="4rem" flexDirection="column" gap="2rem">
      <Alert
        testId="two-factor-action-warning"
        type="warning"
        message={t('Page.StorageUnit.Actions.TwoFactor.ActionWarning')}
      />
      {remainingAttempts !== undefined && (
        <Alert
          testId="two-factor-action-attempts"
          type={twoFactorAttemptsAlertType(remainingAttempts)}
          message={t(
            // Not the pin dialog's texts for the two serious states: those address someone about to
            // type a code (exhausted sends them to the call centre, last attempt warns them not to
            // mistype), while here nobody types anything - the operator IS the call centre and the
            // "next mistake" would be the recipient's. Only the calm counter reads the same on both.
            remainingAttempts === 0
              ? 'Page.StorageUnit.Actions.TwoFactor.ActionAttempts.Exhausted'
              : remainingAttempts === 1
                ? 'Page.StorageUnit.Actions.TwoFactor.ActionAttempts.LastAttempt'
                : 'Page.StorageUnit.Actions.TwoFactor.Attempts',
            {
              failedAttempts: String(failedAttempts),
              remainingAttempts: String(remainingAttempts),
              maxAttempts: String(MAX_TWO_FACTOR_ATTEMPTS),
            }
          )}
        />
      )}
    </Flex>
  );
};
